import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import SafeHTML from '../components/SafeHTML';
import { getFaqKey } from '../utils/keys';

const UAEGoldenVisaPage = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: t('gvFaq1Q'),
      answer: t('gvFaq1A')
    },
    {
      question: t('gvFaq2Q'),
      answer: t('gvFaq2A')
    },
    {
      question: t('gvFaq3Q'),
      answer: t('gvFaq3A')
    },
    {
      question: t('gvFaq4Q'),
      answer: t('gvFaq4A')
    },
    {
      question: t('gvFaq5Q'),
      answer: t('gvFaq5A')
    },
    {
      question: t('gvFaq6Q'),
      answer: t('gvFaq6A')
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section 
        className="relative h-[500px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1726533765275-a69cfd7f9897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxEdWJhaSUyMHNreWxpbmUlMjBtb2Rlcm4lMjBidXNpbmVzcyUyMG9mZmljZXxlbnwwfHx8fDE3NzYzMjgwOTJ8MA&ixlib=rb-4.1.0&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <div className="inline-block bg-yellow-400 text-black px-4 py-2 rounded font-bold mb-4">
              {t('gvBadge')}
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('gvHeroTitle')}
            </h1>
            <p className="text-xl text-white/90 mb-8">
              {t('gvHeroSubtitle')}
            </p>
            <a
              href="https://wa.me/971585862103?text=Hello%2C%20I%20would%20like%20to%20apply%20for%20a%20UAE%20Golden%20Visa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
            >
              {t('gvHeroBtn')}
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        <div className="prose prose-lg max-w-none mb-16">
          <SafeHTML tag="p" className="text-gray-700 text-lg leading-relaxed" html={t('gvIntro1')} />
          <SafeHTML tag="p" className="text-gray-700 text-lg leading-relaxed mt-4" html={t('gvIntro2')} />
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('gvProcessTitle')}</h2>
          
          <div className="space-y-8">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">1</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('gvProcess1Title')}</h3>
                <p className="text-gray-600">{t('gvProcess1Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">2</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('gvProcess2Title')}</h3>
                <p className="text-gray-600">{t('gvProcess2Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">3</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('gvProcess3Title')}</h3>
                <p className="text-gray-600">{t('gvProcess3Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">4</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('gvProcess4Title')}</h3>
                <p className="text-gray-600">{t('gvProcess4Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">5</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('gvProcess5Title')}</h3>
                <p className="text-gray-600">{t('gvProcess5Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">6</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('gvProcess6Title')}</h3>
                <p className="text-gray-600">{t('gvProcess6Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">7</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('gvProcess7Title')}</h3>
                <p className="text-gray-600">{t('gvProcess7Desc')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('gvCategoriesTitle')}</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <SafeHTML tag="span" className="" html={t('gvCategory1')} />
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <SafeHTML tag="span" className="" html={t('gvCategory2')} />
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <SafeHTML tag="span" className="" html={t('gvCategory3')} />
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <SafeHTML tag="span" className="" html={t('gvCategory4')} />
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <SafeHTML tag="span" className="" html={t('gvCategory5')} />
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <SafeHTML tag="span" className="" html={t('gvCategory6')} />
            </li>
          </ul>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('gvFaqTitle')}</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={getFaqKey(faq.question, index)} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 bg-gray-50">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('gvPackagesTitle')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-4 text-left font-bold text-gray-900">{t('features')}</th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">
                    <div>{t('gvTableInvestor')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('gvTableInvestorSub')}</div>
                  </th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900 bg-blue-50">
                    <div>{t('gvTableInvestorPlus')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('gvTableInvestorPlusSub')}</div>
                  </th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">
                    <div>{t('gvTableSpecialized')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('gvTableSpecializedSub')}</div>
                  </th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">
                    <div>{t('gvTableVip')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('gvTableVipSub')}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('gvFeature1')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('gvFeature2')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('gvFeature3')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('gvFeature4')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('gvFeature5')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('gvFeature6')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('gvFeature7')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('gvFeature8')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-4"></td>
                  <td className="border border-gray-300 p-4 text-center">
                    <a href="https://wa.me/971585862103?text=I%20want%20Investor%20Golden%20Visa" target="_blank" rel="noopener noreferrer" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded font-semibold transition-colors">{t('saGetInTouch')}</a>
                  </td>
                  <td className="border border-gray-300 p-4 text-center bg-blue-50">
                    <a href="https://wa.me/971585862103?text=I%20want%20Investor%20Plus%20Golden%20Visa" target="_blank" rel="noopener noreferrer" className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan hover:shadow-lg text-white px-6 py-2 rounded font-semibold transition-all">{t('saGetInTouch')}</a>
                  </td>
                  <td className="border border-gray-300 p-4 text-center">
                    <a href="https://wa.me/971585862103?text=I%20want%20Specialized%20Talent%20Golden%20Visa" target="_blank" rel="noopener noreferrer" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded font-semibold transition-colors">{t('saGetInTouch')}</a>
                  </td>
                  <td className="border border-gray-300 p-4 text-center">
                    <a href="https://wa.me/971585862103?text=I%20want%20VIP%20Golden%20Visa%20Concierge" target="_blank" rel="noopener noreferrer" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded font-semibold transition-colors">{t('saGetInTouch')}</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default UAEGoldenVisaPage;
